import SaveOutlined from "@mui/icons-material/SaveOutlined";
import { type FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SaveAsDefaultDialog } from "../../components/SaveAsDefault/SaveAsDefaultDialog";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { PrintoutSaveAsDefaultNotice } from "./PrintoutSaveAsDefaultNotice";
import { UseDepartmentPrintoutsButton } from "./UseDepartmentPrintoutsButton";
import { useDepartmentPrintouts } from "./useDepartmentPrintouts";

type Props = {
	/** Save as default — support only in this popup. */
	showSaveAsDefault: boolean;
	/** Use department printouts — anyone who can have saved printouts. */
	showUseDepartment: boolean;
	/** Printout 1's own save is on its way (its autosave included), as the Save as default button always showed. */
	isUpdatingSettings: boolean;
	/** Waits for any saved-printout save still on its way; resolves whether it worked. */
	flushPresetSave: () => Promise<boolean>;
	/** Sends Printout 1's waiting autosave now, if there is one; resolves whether it worked. */
	flushPrintout1Save: () => Promise<boolean>;
	/** Printout 1's Save as default, with the saved printouts included. Rejects on failure. */
	savePrintout1AsDefault: () => Promise<void>;
	/** Back on Printout 1 with the saved-printout list reloaded. */
	resetToDepartmentPrintouts: () => Promise<void>;
	/** Save as default worked: the popup closes, as it always did after one. */
	onSavedAsDefault: () => void;
	/** The department's settings are in the user-settings cache: the popup can preview them. */
	onDepartmentSettingsRestored: () => void;
};

/**
 * The popup footer's department actions — Save as default (with its confirm, which counts who loses their
 * own saved printouts) and Use department printouts. Both confirms stay open, spinning, until the action
 * has finished: they're modal, so nothing in the popup can be edited mid-way and saved over the result.
 */
export const PrintoutDepartmentActions: FC<Props> = ({
	showSaveAsDefault,
	showUseDepartment,
	isUpdatingSettings,
	flushPresetSave,
	flushPrintout1Save,
	savePrintout1AsDefault,
	resetToDepartmentPrintouts,
	onSavedAsDefault,
	onDepartmentSettingsRestored,
}) => {
	const { t } = useTranslation();
	const { saveAsDefault, restoreDepartmentPrintouts, isSavingAsDefault, isRestoring } = useDepartmentPrintouts({
		flushPresetSave,
		flushPrintout1Save,
		savePrintout1AsDefault,
		onDepartmentSettingsRestored,
		resetToDepartmentPrintouts,
	});
	const [isSaveDialogOpen, setIsSaveDialogOpen] = useState<boolean>(false);
	/** A second confirm click in the same frame (before the spinner renders) must not start a second save. */
	const savingRef = useRef<boolean>(false);

	const confirmSaveAsDefault = async (): Promise<void> => {
		if (savingRef.current) return;
		savingRef.current = true;
		try {
			// Closes the popup only once it worked: a failure is on screen as a snackbar, with the popup there to retry from.
			if (await saveAsDefault()) onSavedAsDefault();
		} finally {
			savingRef.current = false;
			setIsSaveDialogOpen(false);
		}
	};

	return (
		<>
			{showSaveAsDefault && (
				<>
					<SystemButton
						data-testid="printout-save-as-default"
						buttonVariant="secondary"
						size="small"
						onClick={() => setIsSaveDialogOpen(true)}
						loading={isUpdatingSettings || isSavingAsDefault}
						leadingIcon={<SaveOutlined />}
					>
						{t("settings.save.as.default")}
					</SystemButton>
					<SaveAsDefaultDialog
						open={isSaveDialogOpen}
						onClose={() => {
							if (!savingRef.current) setIsSaveDialogOpen(false);
						}}
						onConfirm={() => void confirmSaveAsDefault()}
						dataTestId="printout-save-as-default-dialog"
						body={<PrintoutSaveAsDefaultNotice open={isSaveDialogOpen} />}
						confirmLoading={isSavingAsDefault}
					/>
				</>
			)}
			{showUseDepartment && <UseDepartmentPrintoutsButton onConfirm={restoreDepartmentPrintouts} running={isRestoring} />}
		</>
	);
};
